import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Layout } from "../components/Layout";
import { LeaderboardDialog } from "../components/LeaderboardDialog";
import { SessionDetailDialog } from "../components/SessionDetailDialog";
import { HistoryEntrySource } from "../components/performance/HistoryEntrySource";
import { SportTabs } from "../components/sports/SportTabs";
import { ModelBadge } from "../components/sportConfig/ModelBadge";
import { findIpfTotal, IpfTotalLine } from "../components/performance/IpfTotalLine";
import {
	coefficientMetric,
	LeaderboardBodyweightFilters,
} from "../components/performance/LeaderboardBodyweightFilters";
import { fetchAPI } from "../lib/utils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../components/ui/dialog";
import {
	Target,
	Trophy,
	Clock,
	Dumbbell,
	Zap,
	Play,
	Image,
	Edit2,
	Trash2,
	ExternalLink,
	ClipboardList,
	Check,
	X,
	Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
	formatIntervalSecondsLabel,
	getDisplayEmomRounds,
	getEmomRoundLabel,
	formatEmomItemSummary,
	formatAmrapScoreWithRoundsLabel,
} from "../lib/conditioningResultUtils";
import { getImageUrl, getYouTubeEmbedUrl } from "../lib/coachUtils";
import {
	ResponsiveContainer,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
	LineChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Line,
} from "recharts";
import {
	useChartColors,
	useChartTooltipStyle,
	useReadableColor,
} from "../lib/useChartColors";
import { LevelsGuideButton } from "../components/performance/LevelsGuide";

export default function PerformanceProfile() {
	const chartColors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	/* I colori dei domini li sceglie il coach e sono tarati sul fondo
	   scuro: su fondo chiaro vanno scuriti quanto basta per leggerli. */
	const readable = useReadableColor();
	const { user } = useAuth();
	const navigate = useNavigate();
	const { t, language } = useLanguage();
	const userId = user?.user_id;

	// Performance Profile state
	const [performanceProfile, setPerformanceProfile] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingPpExercise, setEditingPpExercise] = useState(null);
	const [ppManualValue, setPpManualValue] = useState("");
	const [ppManualMinutes, setPpManualMinutes] = useState("");
	const [ppManualSeconds, setPpManualSeconds] = useState("");
	// Un salvataggio alla volta: il ref blocca la seconda chiamata anche quando
	// Invio e il tap sul check arrivano nello stesso tick, prima del re-render.
	const [ppSaving, setPpSaving] = useState(false);
	const ppSavingRef = useRef(false);
	const [activePpDomainTab, setActivePpDomainTab] = useState("general");
	// Un atleta puo' praticare piu' discipline, ognuna con il PROPRIO profilo
	// prestativo: domini, esercizi e tabelle di riferimento diverse. Lo stesso
	// atleta puo' essere forte su una scala e indietro su un'altra, ed e'
	// esattamente l'informazione che si vuole vedere — per questo i profili
	// restano separati per sport e non si fondono.
	// Spec: docs/shooting/08-atleta-multisport.md
	const [activeSport, setActiveSport] = useState(null);
	const [sportsOptions, setSportsOptions] = useState([]);
	const [ppLeaderboardOpen, setPpLeaderboardOpen] = useState(false);
	const [ppLeaderboardExercise, setPpLeaderboardExercise] = useState(null);
	const [ppLeaderboardData, setPpLeaderboardData] = useState(null);
	const [ppLeaderboardLoading, setPpLeaderboardLoading] = useState(false);
	const [ppLeaderboardError, setPpLeaderboardError] = useState("");
	const [ppLeaderboardGenderFilter, setPpLeaderboardGenderFilter] =
		useState("all");
	const [ppLeaderboardGroupId, setPpLeaderboardGroupId] = useState("");
	// Peso corporeo in classifica (D14 di docs/sport-config): categoria e
	// ordinamento per coefficiente, solo se il modello li usa. Le opzioni
	// restano visibili mentre si ricarica, invece di sparire a ogni filtro.
	const [ppLeaderboardClassFilter, setPpLeaderboardClassFilter] = useState("");
	const [ppLeaderboardSort, setPpLeaderboardSort] = useState("value");
	const [ppLeaderboardBodyweight, setPpLeaderboardBodyweight] = useState(null);
	const [performanceGroups, setPerformanceGroups] = useState([]);
	const [ppHistoryOpen, setPpHistoryOpen] = useState(false);
	const [ppHistoryExercise, setPpHistoryExercise] = useState(null);
	const [ppHistoryData, setPpHistoryData] = useState(null);
	const [ppHistoryLoading, setPpHistoryLoading] = useState(false);
	const [ppHistoryError, setPpHistoryError] = useState("");
	const [ppHistoryEditingId, setPpHistoryEditingId] = useState(null);
	const [ppHistorySavingId, setPpHistorySavingId] = useState(null);
	const [ppHistoryRange, setPpHistoryRange] = useState("all");
	const [ppSessionResultId, setPpSessionResultId] = useState(null);
	const [ppHistoryDraft, setPpHistoryDraft] = useState({
		date: "",
		value: "",
		raw_weight: "",
		raw_reps: "",
		notes: "",
	});
	const [conditioningPreviewExercise, setConditioningPreviewExercise] =
		useState(null);
	const [exerciseLibrary, setExerciseLibrary] = useState([]);
	const [mediaExercise, setMediaExercise] = useState(null);

	const exerciseLibraryMap = useMemo(() => {
		const map = {};
		for (const ex of exerciseLibrary || []) {
			if (ex?.exercise_id) map[ex.exercise_id] = ex;
			const nameKey = String(ex?.name || "")
				.trim()
				.toLowerCase();
			if (nameKey) map[`_name_${nameKey}`] = ex;
		}
		return map;
	}, [exerciseLibrary]);

	const athleteSports = useMemo(() => {
		if (Array.isArray(user?.sports) && user.sports.length > 0)
			return user.sports;
		return user?.sport ? [user.sport] : [];
	}, [user?.sports, user?.sport]);

	// La primaria e' il primo elemento. `activeSport` resta null finche' non si
	// conosce la lista, cosi' la prima fetch non parte con uno sport sbagliato.
	const currentSport = activeSport || athleteSports[0] || null;

	useEffect(() => {
		if (!userId) return;

		let cancelled = false;
		fetchAPI("/profile/assignments")
			.then((data) => {
				if (!cancelled) {
					setPerformanceGroups(Array.isArray(data?.groups) ? data.groups : []);
				}
			})
			.catch(() => {
				if (!cancelled) setPerformanceGroups([]);
			});

		return () => {
			cancelled = true;
		};
	}, [userId]);

	// Suffisso `?sport=` da appendere a ogni rotta per-sport. Vuoto con un solo
	// sport: il server ripiega sulla primaria e le chiamate restano identiche.
	const sportQuery = useMemo(
		() =>
			athleteSports.length > 1 && currentSport
				? `?sport=${encodeURIComponent(currentSport)}`
				: "",
		[athleteSports.length, currentSport],
	);

	// Le etichette delle discipline sono dato admin: non si traducono
	// (invariante i18n del repo), si leggono dal pool centralizzato.
	useEffect(() => {
		if (athleteSports.length < 2) return;
		let cancelled = false;
		fetchAPI("/users/sports/list")
			.then((data) => {
				if (!cancelled) setSportsOptions(Array.isArray(data) ? data : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [athleteSports.length]);

	const handleSportTabChange = (nextSport) => {
		if (nextSport === currentSport) return;
		setActiveSport(nextSport);
		// I domini sono diversi per sport: quello attivo non esiste nell'altro.
		setActivePpDomainTab("general");
	};

	const sportLabelFor = (value) => {
		const found = sportsOptions.find((s) => s.value === value);
		return found?.label || value;
	};

	// Load performance profile data on mount
	useEffect(() => {
		if (!userId) return;

		let cancelled = false;

		const loadPerformanceProfile = async () => {
			setLoading(true);
			try {
				const ppData = await fetchAPI(
					`/profile/performance-profile${sportQuery}`,
				).catch(() => []);
				if (!cancelled) {
					setPerformanceProfile(Array.isArray(ppData) ? ppData : []);
				}
			} catch (error) {
				if (!cancelled) {
					console.error("Failed to load performance profile:", error);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadPerformanceProfile();

		return () => {
			cancelled = true;
		};
		// `sportQuery` cambia quando l'atleta passa a un altro tab sport: il
		// profilo va ricaricato, sono domini ed esercizi completamente diversi.
	}, [userId, sportQuery]);

	useEffect(() => {
		if (!userId) return;

		let cancelled = false;

		const loadExerciseLibrary = async () => {
			try {
				const list = await fetchAPI("/exercises").catch(() => []);
				if (!cancelled) {
					setExerciseLibrary(Array.isArray(list) ? list : []);
				}
			} catch {
				if (!cancelled) {
					setExerciseLibrary([]);
				}
			}
		};

		loadExerciseLibrary();

		return () => {
			cancelled = true;
		};
	}, [userId]);

	// Ricarica il profilo senza mai azzerarlo: se la rilettura fallisce resta
	// in pagina quello che c'era. Prima un errore di rete dopo un salvataggio
	// riuscito svuotava la lista, e l'atleta reinseriva il valore.
	const reloadPerformanceProfile = async () => {
		const ppData = await fetchAPI(
			`/profile/performance-profile${sportQuery}`,
		).catch(() => null);
		if (Array.isArray(ppData)) setPerformanceProfile(ppData);
	};

	const closeManualEditor = () => {
		setEditingPpExercise(null);
		setPpManualValue("");
		setPpManualMinutes("");
		setPpManualSeconds("");
	};

	// Apre l'editor gia' compilato con il manuale corrente: cosi' si vede che
	// un valore c'e' gia' e non lo si ridigita per sicurezza.
	const openManualEditor = (ex) => {
		setEditingPpExercise(ex.exercise_id);
		const current = ex.manual_value;
		if (current === null || current === undefined || current === "") {
			setPpManualValue("");
			setPpManualMinutes("");
			setPpManualSeconds("");
		} else if (ex.unit === "minutes_seconds") {
			const total = Math.max(0, Math.round(Number(current) || 0));
			setPpManualValue("");
			setPpManualMinutes(String(Math.floor(total / 60)));
			setPpManualSeconds(String(total % 60));
		} else {
			setPpManualValue(String(current));
			setPpManualMinutes("");
			setPpManualSeconds("");
		}
	};

	const handleSaveManualValue = async (exerciseId) => {
		if (ppSavingRef.current) return;
		// Find the exercise to check its unit
		let exerciseUnit = "kg";
		let currentExercise = null;
		for (const domain of performanceProfile) {
			const ex = domain.exercises?.find((e) => e.exercise_id === exerciseId);
			if (ex) {
				exerciseUnit = ex.unit;
				currentExercise = ex;
				break;
			}
		}

		let val;
		if (exerciseUnit === "minutes_seconds") {
			val = convertToSeconds(ppManualMinutes, ppManualSeconds);
			if (isNaN(val) || val < 0) {
				toast.error(t("enterValidNumeric"));
				return;
			}
		} else {
			val = parseFloat(ppManualValue);
			if (isNaN(val)) {
				toast.error(t("enterValidNumeric"));
				return;
			}
		}

		const today = new Date().toISOString().split("T")[0];
		// Stesso valore gia' salvato oggi: niente da scrivere, si chiude e basta.
		if (
			currentExercise &&
			Number(currentExercise.manual_value) === val &&
			currentExercise.manual_date === today
		) {
			toast.info(t("manualValueUnchanged"));
			closeManualEditor();
			return;
		}

		ppSavingRef.current = true;
		setPpSaving(true);
		try {
			// `sportQuery` dice al server in quale profilo cercare l'esercizio:
			// senza, cercava solo nella primaria e il massimale di un benchmark
			// del secondo sport tornava 404 (B6 di docs/sport-config).
			await fetchAPI(
				`/profile/performance-profile/values/${exerciseId}${sportQuery}`,
				{
					method: "PUT",
					body: JSON.stringify({ value: val, date: today }),
				},
			);
			toast.success(t("valueSaved"));
			closeManualEditor();
			await reloadPerformanceProfile();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		} finally {
			ppSavingRef.current = false;
			setPpSaving(false);
		}
	};

	const handleDeleteManualValue = async (exerciseId) => {
		try {
			await fetchAPI(`/profile/performance-profile/values/${exerciseId}`, {
				method: "DELETE",
			});
			toast.success(t("manualValueRemoved"));
			await reloadPerformanceProfile();
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		}
	};

	const handleOpenPerformanceLeaderboard = (exercise) => {
		if (!exercise?.exercise_id) return;
		setPpLeaderboardExercise(exercise);
		setPpLeaderboardGenderFilter("all");
		setPpLeaderboardGroupId("");
		setPpLeaderboardClassFilter("");
		setPpLeaderboardSort("value");
		setPpLeaderboardBodyweight(null);
		setPpLeaderboardOpen(true);
	};

	const handleOpenPerformanceHistory = (exercise) => {
		if (!exercise?.exercise_id) return;
		setPpHistoryExercise(exercise);
		setPpHistoryData(null);
		setPpHistoryError("");
		setPpHistoryEditingId(null);
		setPpHistoryOpen(true);
	};

	const reloadPerformanceHistory = async () => {
		if (!ppHistoryExercise?.exercise_id) return;
		const data = await fetchAPI(
			`/profile/performance-profile/history/${encodeURIComponent(ppHistoryExercise.exercise_id)}${sportQuery}`,
		);
		setPpHistoryData(data || null);
	};

	const getPpHistoryItemKey = (item) => {
		if (!item) return "";
		if (item?.result_id) {
			return `${item.result_id}:${item?.superset_detail_index ?? "direct"}`;
		}
		return `${item?.entry_type || "item"}:${item?.date || ""}:${item?.superset_detail_index ?? "na"}`;
	};

	const handleStartEditHistoryItem = (item) => {
		if (!item?.result_id || !item?.can_edit) return;
		setPpHistoryEditingId(getPpHistoryItemKey(item));
		setPpHistoryDraft({
			date: item.date || "",
			value: item.value != null ? String(item.value) : "",
			raw_weight: item.raw_weight != null ? String(item.raw_weight) : "",
			raw_reps: item.raw_reps != null ? String(item.raw_reps) : "",
			notes: item.notes || "",
		});
	};

	const handleSaveHistoryItem = async (item) => {
		if (!item?.result_id || !item?.can_edit || !ppHistoryData?.unit) return;
		if (
			item.affects_other_movements &&
			!window.confirm(t("historySharedEditConfirm"))
		)
			return;
		const itemKey = getPpHistoryItemKey(item);

		const payload = {
			superset_detail_index: Number.isInteger(item?.superset_detail_index)
				? item.superset_detail_index
				: undefined,
			date: ppHistoryDraft.date || undefined,
			notes: ppHistoryDraft.notes || "",
		};

		if (ppHistoryData.unit === "kg") {
			payload.weight_kg = ppHistoryDraft.raw_weight
				? Number(ppHistoryDraft.raw_weight)
				: null;
			payload.reps = ppHistoryDraft.raw_reps
				? Number(ppHistoryDraft.raw_reps)
				: null;
		} else if (ppHistoryData.unit === "reps") {
			payload.reps = ppHistoryDraft.value ? Number(ppHistoryDraft.value) : null;
		} else if (
			ppHistoryData.unit === "seconds" ||
			ppHistoryData.unit === "minutes_seconds"
		) {
			payload.time_seconds = ppHistoryDraft.value
				? Number(ppHistoryDraft.value)
				: null;
		} else {
			payload.score = ppHistoryDraft.value
				? Number(ppHistoryDraft.value)
				: null;
		}

		setPpHistorySavingId(itemKey);
		try {
			await fetchAPI(
				`/profile/performance-profile/history/diary/${item.result_id}`,
				{
					method: "PUT",
					body: JSON.stringify(payload),
				},
			);
			await reloadPerformanceHistory();
			setPpHistoryEditingId(null);
			toast.success(t("historyEntryUpdated"));
		} catch (error) {
			toast.error(error?.message || t("error"));
		} finally {
			setPpHistorySavingId(null);
		}
	};

	const handleDeleteHistoryItem = async (item) => {
		if (!item?.result_id || !item?.can_delete) return;
		const itemKey = getPpHistoryItemKey(item);
		const ok = window.confirm(
			t(
				item.affects_other_movements
					? "historySharedDeleteConfirm"
					: "historyDeleteConfirm",
			),
		);
		if (!ok) return;

		setPpHistorySavingId(itemKey);
		try {
			const query = Number.isInteger(item?.superset_detail_index)
				? `?superset_detail_index=${item.superset_detail_index}`
				: "";
			await fetchAPI(
				`/profile/performance-profile/history/diary/${item.result_id}${query}`,
				{
					method: "DELETE",
				},
			);
			await reloadPerformanceHistory();
			if (ppHistoryEditingId === itemKey) {
				setPpHistoryEditingId(null);
			}
			toast.success(t("historyEntryDeleted"));
		} catch (error) {
			toast.error(error?.message || t("error"));
		} finally {
			setPpHistorySavingId(null);
		}
	};

	useEffect(() => {
		if (!ppLeaderboardOpen || !ppLeaderboardExercise?.exercise_id) return;

		let isActive = true;

		const loadPerformanceLeaderboard = async () => {
			setPpLeaderboardLoading(true);
			setPpLeaderboardError("");
			try {
				const query = new URLSearchParams();
				if (ppLeaderboardGenderFilter !== "all") {
					query.set(
						"gender",
						ppLeaderboardGenderFilter === "female" ? "F" : "M",
					);
				}
				if (athleteSports.length > 1 && currentSport) {
					query.set("sport", currentSport);
				}
				if (ppLeaderboardGroupId) {
					query.set("group_id", ppLeaderboardGroupId);
				}
				if (ppLeaderboardClassFilter) {
					query.set("weight_class", ppLeaderboardClassFilter);
				}
				if (ppLeaderboardSort === "coefficient") {
					query.set("sort", "coefficient");
				}
				const suffix = query.toString() ? `?${query.toString()}` : "";
				const data = await fetchAPI(
					`/profile/performance-profile/leaderboard/${encodeURIComponent(ppLeaderboardExercise.exercise_id)}${suffix}`,
				);
				if (isActive) {
					setPpLeaderboardData(data || null);
					setPpLeaderboardBodyweight(data?.bodyweight || null);
				}
			} catch (error) {
				if (isActive) {
					setPpLeaderboardData(null);
					setPpLeaderboardError(
						error?.message || t("performanceLeaderboardLoadError"),
					);
				}
			} finally {
				if (isActive) {
					setPpLeaderboardLoading(false);
				}
			}
		};

		loadPerformanceLeaderboard();

		return () => {
			isActive = false;
		};
		// `currentSport` fa parte della chiave: cambiando tab sport la classifica
		// e' un'altra, e senza la dipendenza resterebbe quella di prima.
	}, [
		ppLeaderboardOpen,
		ppLeaderboardExercise,
		ppLeaderboardGenderFilter,
		ppLeaderboardGroupId,
		ppLeaderboardClassFilter,
		ppLeaderboardSort,
		athleteSports.length,
		currentSport,
		t,
	]);

	useEffect(() => {
		if (!ppHistoryOpen || !ppHistoryExercise?.exercise_id) return;

		let isActive = true;

		const loadPerformanceHistory = async () => {
			setPpHistoryLoading(true);
			setPpHistoryError("");
			try {
				const data = await fetchAPI(
					`/profile/performance-profile/history/${encodeURIComponent(ppHistoryExercise.exercise_id)}${sportQuery}`,
				);
				if (isActive) {
					setPpHistoryData(data || null);
				}
			} catch (error) {
				if (isActive) {
					setPpHistoryData(null);
					setPpHistoryError(error?.message || t("historyLoadError"));
				}
			} finally {
				if (isActive) {
					setPpHistoryLoading(false);
				}
			}
		};

		loadPerformanceHistory();

		return () => {
			isActive = false;
		};
		// Idem per lo storico: `sportQuery` seleziona la configurazione di quale
		// disciplina usare per risolvere l'esercizio.
	}, [ppHistoryOpen, ppHistoryExercise, sportQuery, t]);

	const getUnitLabel = (unit) => {
		const labels = {
			kg: "kg",
			reps: "reps",
			seconds: "s",
			minutes: "min",
			minutes_seconds: "min:sec",
			meters: "m",
			points: "pts",
			hit_factor: "HF",
		};
		return labels[unit] || unit;
	};

	const getPerformanceSourceLabel = (exercise) => {
		if (!exercise?.source) return "";
		if (exercise.source === "manual") {
			return t("manualEntry");
		}
		if (exercise.source === "1rm_test") return t("source1rmExact");
		if (exercise.source === "auto_1rm_estimate") return t("source1rmEstimate");
		return t("autoFromResults");
	};

	const getPerformanceSourceInfo = (exercise) => {
		if (!exercise?.source) return "";
		const primaryDate =
			exercise.source === "manual" ? exercise.manual_date : exercise.auto_date;
		let detail = "";
		if (exercise.source !== "manual" && exercise.auto_detail) {
			detail = exercise.auto_detail;
		}
		if (
			exercise.source === "manual" &&
			exercise.source_reason === "manual_highest" &&
			exercise.auto_detail
		) {
			detail = `${t("estimated1RM")}: ${exercise.auto_detail}`;
		}
		return {
			date: primaryDate || "",
			detail,
		};
	};

	const getAutoHigherSourceLabel = (exercise) => {
		if (exercise?.source === "auto_1rm_estimate") {
			return t("sourceAutoHigherEstimate");
		}
		return t("sourceAutoHigher");
	};

	const formatMinutesSeconds = (totalSeconds) => {
		if (totalSeconds === null || totalSeconds === undefined) return "—";
		const mins = Math.floor(totalSeconds / 60);
		const secs = Math.round(totalSeconds % 60);
		return `${mins}:${String(secs).padStart(2, "0")}`;
	};

	const convertToSeconds = (minutes, seconds) => {
		const m = parseInt(minutes, 10) || 0;
		const s = parseInt(seconds, 10) || 0;
		return m * 60 + s;
	};

	const renderHistoryEntryValue = (entry, unit) => {
		if (!entry) return "—";
		const v = entry.value;
		if (unit === "kg") {
			// Diary entries: raw_weight + raw_reps; manual entries: only value
			const weight = entry.raw_weight ?? entry.value;
			const reps = entry.raw_reps;
			if (weight == null) return "—";
			if (reps != null) return `${weight} kg × ${reps}`;
			return `${weight} kg`;
		}
		if (v == null) return "—";
		if (unit === "minutes_seconds" || unit === "seconds") {
			return formatMinutesSeconds(v);
		}
		if (unit === "minutes") {
			return `${v} min`;
		}
		if (unit === "reps") return `${v} reps`;
		if (unit === "meters") return `${v} m`;
		if (unit === "points") return `${v} pts`;
		// Rapporto punti/tempo: i decimali sono il dato, non decorazione.
		if (unit === "hit_factor") {
			const parsed = Number(v);
			return Number.isFinite(parsed) ? `HF ${parsed.toFixed(2)}` : String(v);
		}
		return String(v);
	};

	const canViewPerformanceLeaderboard =
		!!user?.performance_public && !!user?.sport;

	// Con un solo dominio il tab "Generale" mostra le stesse card del dominio,
	// senza il radar: il dominio e' la vista d'insieme, si apre direttamente
	// (la barra dei tab non compare). Con piu' domini il tab resta scelta.
	const soleDomainId =
		performanceProfile.length === 1 ? performanceProfile[0].domain_id : null;
	const domainTab = soleDomainId ?? activePpDomainTab;

	const selectedPerformanceDomain = useMemo(() => {
		if (domainTab === "general") return null;
		return (
			performanceProfile.find((domain) => domain.domain_id === domainTab) ||
			null
		);
	}, [domainTab, performanceProfile]);

	const visiblePerformanceDomains = useMemo(() => {
		if (domainTab === "general") return performanceProfile;
		return selectedPerformanceDomain ? [selectedPerformanceDomain] : [];
	}, [domainTab, performanceProfile, selectedPerformanceDomain]);

	const ppHistoryRawItems = useMemo(() => {
		if (!ppHistoryData) return [];
		if (Array.isArray(ppHistoryData.items)) return ppHistoryData.items;
		if (Array.isArray(ppHistoryData.entries)) return ppHistoryData.entries;
		return [];
	}, [ppHistoryData]);

	const ppHistoryCutoffDate = useMemo(() => {
		if (ppHistoryRange === "all") return null;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const cutoff = new Date(today);
		if (ppHistoryRange === "30d") {
			cutoff.setDate(cutoff.getDate() - 30);
			return cutoff;
		}
		if (ppHistoryRange === "6m") {
			cutoff.setMonth(cutoff.getMonth() - 6);
			return cutoff;
		}
		if (ppHistoryRange === "1y") {
			cutoff.setFullYear(cutoff.getFullYear() - 1);
			return cutoff;
		}
		return null;
	}, [ppHistoryRange]);

	const ppHistoryItems = useMemo(() => {
		if (!ppHistoryCutoffDate) return ppHistoryRawItems;
		return ppHistoryRawItems.filter((entry) => {
			if (!entry?.date) return true;
			const entryDate = new Date(entry.date);
			if (Number.isNaN(entryDate.getTime())) return true;
			entryDate.setHours(0, 0, 0, 0);
			return entryDate >= ppHistoryCutoffDate;
		});
	}, [ppHistoryCutoffDate, ppHistoryRawItems]);

	const ppHistoryChartData = useMemo(() => {
		if (!ppHistoryData?.unit) return [];
		if (ppHistoryData.unit === "kg") {
			return ppHistoryItems
				.map((entry) => {
					const weight = Number(
						entry?.raw_weight ?? entry?.weight_kg ?? entry?.value,
					);
					const reps = Number(entry?.raw_reps ?? entry?.reps);
					const providedEstimated = Number(entry?.estimated_1rm);
					const hasWeight = Number.isFinite(weight) && weight > 0;
					const hasProvidedEstimated =
						Number.isFinite(providedEstimated) && providedEstimated > 0;
					const estimated1rm = hasProvidedEstimated
						? providedEstimated
						: hasWeight && Number.isFinite(reps) && reps > 0
							? Math.round(weight * (1 + reps / 30) * 10) / 10
							: null;

					return {
						date: entry.date ? String(entry.date).slice(5, 10) : "",
						weight: hasWeight ? weight : null,
						estimated1rm,
						reps: Number.isFinite(reps) && reps > 0 ? reps : null,
					};
				})
				.filter((entry) => Number.isFinite(entry.weight) && entry.weight > 0)
				.slice(0, 20)
				.reverse();
		}

		return ppHistoryItems
			.filter((entry) => entry?.value !== null && entry?.value !== undefined)
			.slice(0, 20)
			.reverse()
			.map((entry) => ({
				date: entry.date ? String(entry.date).slice(5, 10) : "",
				value: Number(entry.value),
			}));
	}, [ppHistoryData, ppHistoryItems]);

	useEffect(() => {
		if (activePpDomainTab === "general") return;
		const exists = performanceProfile.some(
			(domain) => domain.domain_id === activePpDomainTab,
		);
		if (!exists) {
			setActivePpDomainTab("general");
		}
	}, [activePpDomainTab, performanceProfile]);

	if (loading) {
		return (
			<Layout>
				<div className="flex items-center justify-center min-h-[60vh]">
					<Loader2 className="w-6 h-6 animate-spin text-volt" />
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="max-w-2xl mx-auto px-4 py-5 pb-28">
				{!canViewPerformanceLeaderboard && (
					<Card className="bg-card border-border rounded-sm mb-4">
						<CardContent className="p-3">
							<p className="text-xs text-muted-foreground">
								{t("performancePublicLeaderboardHint")}
							</p>
						</CardContent>
					</Card>
				)}

				{/* Un tab per sport. Ogni disciplina ha domini, esercizi e tabelle di
				    riferimento propri: sono profili distinti, non filtri dello stesso.
				    Con un solo sport la barra non compare e la pagina resta com'era. */}
				<SportTabs
					sports={athleteSports}
					active={currentSport}
					onChange={handleSportTabChange}
					labelFor={sportLabelFor}
					className="mb-4"
				/>
				{/* La categoria di peso, nei modelli che la usano: una riga, la
				    spiegazione in una finestra. Il modello no: per l'atleta e'
				    trasparente. */}
				{currentSport && (
					<div className="mb-3">
						<ModelBadge sport={currentSport} />
					</div>
				)}
				{/* Il totale IPF GL, nei modelli di powerlifting che lo usano (D6b). */}
				{findIpfTotal(performanceProfile) && (
					<div className="mb-3">
						<IpfTotalLine domains={performanceProfile} />
					</div>
				)}

				{/* Senza disciplina il profilo e' strutturalmente vuoto: domini,
				    esercizi e massimali manuali sono configurati per sport, e il
				    backend rifiuta il valore manuale con "No sport selected".
				    La pagina muta lasciava credere di aver inserito un massimale
				    che il sistema non ha mai potuto accettare: qui lo si dice,
				    con la scorciatoia per rimediare. */}
				{athleteSports.length === 0 && !loading && (
					<Card className="bg-card border-border rounded-sm mb-4">
						<CardContent className="p-4 space-y-3">
							<p className="text-sm font-medium text-foreground">
								{t("performanceNoSportTitle")}
							</p>
							<p className="text-sm text-muted-foreground">
								{t("performanceNoSportHint")}
							</p>
							<Button size="sm" onClick={() => navigate("/profile")}>
								{t("performanceNoSportCta")}
							</Button>
						</CardContent>
					</Card>
				)}

				{/* Con una disciplina sola il messaggio mancava: la pagina restava
				    vuota e identica al caso "nessuno sport". */}
				{athleteSports.length > 0 &&
					!loading &&
					performanceProfile.length === 0 && (
						<Card className="bg-card border-border rounded-sm mb-4">
							<CardContent className="p-4">
								<p className="text-sm text-muted-foreground">
									{t("performanceNoDomainsForSport")}
								</p>
							</CardContent>
						</Card>
					)}

				{performanceProfile.length > 1 && (
					<div className="flex gap-1.5 overflow-x-auto pb-1 mb-4 no-scrollbar">
						<Button
							variant={
								activePpDomainTab === "general" ? "default" : "secondary"
							}
							size="sm"
							className={`rounded-sm shrink-0 ${
								activePpDomainTab === "general"
									? "bg-volt hover:bg-volt/90"
									: ""
							}`}
							onClick={() => setActivePpDomainTab("general")}
						>
							{t("performanceGeneralTab")}
						</Button>
						{performanceProfile.map((domain) => (
							<Button
								key={`pp_tab_${domain.domain_id}`}
								variant={
									activePpDomainTab === domain.domain_id
										? "default"
										: "secondary"
								}
								size="sm"
								className={`rounded-sm shrink-0 ${
									activePpDomainTab === domain.domain_id
										? "bg-volt hover:bg-volt/90"
										: ""
								}`}
								onClick={() => setActivePpDomainTab(domain.domain_id)}
							>
								{domain.name}
							</Button>
						))}
					</div>
				)}

				{performanceProfile.length > 1 && activePpDomainTab === "general" && (
					<Card className="bg-card border-border rounded-sm mb-3">
						<CardContent className="p-3">
							<p className="label-tactical mb-2">
								{t("performanceRadarTitle")}
							</p>
							{(() => {
								const radarData = performanceProfile.map((domain) => {
									const validScores = (domain.exercises || [])
										.map((ex) => Number(ex.score_value))
										.filter((v) => Number.isFinite(v));
									const avg =
										validScores.length > 0
											? validScores.reduce((a, b) => a + b, 0) /
												validScores.length
											: 0;
									return {
										domain: domain.name,
										score: avg,
									};
								});
								const radarMax = Math.max(15, ...radarData.map((d) => d.score));

								return radarData.length >= 2 ? (
									<div className="h-72 w-full">
										<ResponsiveContainer width="100%" height="100%">
											<RadarChart data={radarData}>
												<PolarGrid stroke={chartColors.gridSoft} />
												<PolarAngleAxis
													dataKey="domain"
													tick={{ fill: chartColors.axisMuted, fontSize: 10 }}
												/>
												<PolarRadiusAxis
													angle={30}
													domain={[0, radarMax]}
													tickCount={4}
													tick={{ fill: chartColors.axisFaint, fontSize: 10 }}
												/>
												<Radar
													name="Score"
													dataKey="score"
													stroke={chartColors.volt}
													fill={chartColors.volt}
													fillOpacity={0.35}
												/>
											</RadarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<p className="text-xs text-muted-foreground">
										{t("performanceRadarMissingConfig")}
									</p>
								);
							})()}
						</CardContent>
					</Card>
				)}

				{performanceProfile.length > 0 ? (
					visiblePerformanceDomains.map((domain) => (
						<div key={domain.domain_id} className="space-y-3 mb-4">
							<div className="flex items-center gap-2 mb-2">
								<div
									className="w-2.5 h-5 rounded-sm shrink-0"
									style={{ backgroundColor: domain.color || chartColors.volt }}
								/>
								<p className="min-w-0 font-heading text-sm uppercase tracking-wider">
									{domain.name}
								</p>
								{/* La descrizione sta nella guida: di fianco al nome era
								    troncata, e per alcuni modelli e' lunga. */}
								<LevelsGuideButton domain={domain} />
							</div>

							{domainTab !== "general" &&
								(() => {
									const radarItems = (domain.exercises || [])
										.filter(
											(ex) =>
												ex.score_value !== null &&
												ex.score_value !== undefined &&
												Number.isFinite(Number(ex.score_value)),
										)
										.map((ex) => ({
											exercise: ex.exercise_name,
											score: Number(ex.score_value),
										}));

									const radarMax = Number(domain.scoring_max) || 15;

									return (
										<Card className="bg-card border-border rounded-sm mb-3">
											<CardContent className="p-3">
												<p className="label-tactical mb-2">
													{t("performanceRadarTitle")} {domain.name}
												</p>
												{radarItems.length >= 3 ? (
													<div className="h-72 w-full">
														<ResponsiveContainer width="100%" height="100%">
															<RadarChart data={radarItems}>
																<PolarGrid stroke={chartColors.gridSoft} />
																<PolarAngleAxis
																	dataKey="exercise"
																	tick={{ fill: chartColors.axisMuted, fontSize: 10 }}
																/>
																<PolarRadiusAxis
																	angle={30}
																	domain={[0, radarMax]}
																	tickCount={4}
																	tick={{ fill: chartColors.axisFaint, fontSize: 10 }}
																/>
																<Radar
																	name="Score"
																	dataKey="score"
																	stroke={readable(domain.color || chartColors.volt)}
																	fill={readable(domain.color || chartColors.volt)}
																	fillOpacity={0.35}
																/>
															</RadarChart>
														</ResponsiveContainer>
													</div>
												) : (
													<p className="text-xs text-muted-foreground">
														{t("performanceRadarMissingConfig")}
													</p>
												)}
											</CardContent>
										</Card>
									);
								})()}

							{domain.exercises?.length > 0 ? (
								<div className="space-y-1.5">
									{domain.exercises.map((ex) =>
										(() => {
											const isPpConditioning =
												ex.exercise_id?.startsWith("cond_") ||
												ex.conditioning_type;
											return (
												<Card
													key={ex.config_id}
													className="bg-card border-border rounded-sm"
												>
													<CardContent className="p-3">
														<div className="flex items-center justify-between">
															<div className="flex-1 min-w-0">
																{isPpConditioning ? (
																	<button
																		type="button"
																		onClick={() =>
																			setConditioningPreviewExercise(ex)
																		}
																		className="text-left group"
																		title="Apri dettaglio conditioning"
																	>
																		<p className="text-sm font-medium truncate group-hover:text-volt">
																			{ex.exercise_name}
																		</p>
																		<div className="flex items-center gap-1 text-[10px] text-volt">
																			<ExternalLink className="w-3 h-3" />
																			<span>Tocca per dettagli</span>
																		</div>
																	</button>
																) : (
																	<p className="text-sm font-medium truncate">
																		{ex.exercise_name}
																	</p>
																)}
																{ex.source && (
																	<div className="space-y-1 mt-0.5">
																		{(() => {
																			const sourceInfo =
																				getPerformanceSourceInfo(ex);
																			return (
																				<>
																					<p className="text-[10px] leading-4 text-muted-foreground">
																						{getPerformanceSourceLabel(ex)}
																					</p>
																					{sourceInfo.date && (
																						<p className="text-[10px] leading-4 text-muted-foreground/70">
																							{sourceInfo.date}
																						</p>
																					)}
																					{sourceInfo.detail && (
																						<p className="text-[10px] leading-4 text-muted-foreground/75 break-words">
																							{sourceInfo.detail}
																						</p>
																					)}
																				</>
																			);
																		})()}
																		{ex.source_reason === "auto_highest" &&
																			ex.manual_value != null && (
																				<p className="text-[10px] text-amber-400/80">
																					{getAutoHigherSourceLabel(ex)}
																				</p>
																			)}
																		{ex.source === "manual" &&
																			ex.source_reason === "manual_highest" &&
																			ex.auto_value != null && (
																				<p className="text-[10px] text-muted-foreground/70">
																					{t("sourceManualHigher")}
																				</p>
																			)}
																	</div>
																)}
															</div>

															{editingPpExercise === ex.exercise_id ? (
																<div className="flex items-center gap-1.5 shrink-0 ml-2">
																	{ex.unit === "minutes_seconds" ? (
																		<>
																			<Input
																				type="number"
																				min="0"
																				value={ppManualMinutes}
																				onChange={(e) =>
																					setPpManualMinutes(e.target.value)
																				}
																				className="w-14 h-8 text-sm bg-secondary border-transparent rounded-sm text-center"
																				placeholder="mm"
																				autoFocus
																				disabled={ppSaving}
																				onKeyDown={(e) => {
																					if (e.key === "Enter")
																						handleSaveManualValue(
																							ex.exercise_id,
																						);
																					if (e.key === "Escape") {
																						setEditingPpExercise(null);
																						setPpManualMinutes("");
																						setPpManualSeconds("");
																					}
																				}}
																			/>
																			<span className="text-xs text-muted-foreground">
																				:
																			</span>
																			<Input
																				type="number"
																				min="0"
																				max="59"
																				value={ppManualSeconds}
																				onChange={(e) =>
																					setPpManualSeconds(e.target.value)
																				}
																				className="w-14 h-8 text-sm bg-secondary border-transparent rounded-sm text-center"
																				placeholder="ss"
																				disabled={ppSaving}
																				onKeyDown={(e) => {
																					if (e.key === "Enter")
																						handleSaveManualValue(
																							ex.exercise_id,
																						);
																					if (e.key === "Escape") {
																						setEditingPpExercise(null);
																						setPpManualMinutes("");
																						setPpManualSeconds("");
																					}
																				}}
																			/>
																		</>
																	) : (
																		<Input
																			type="number"
																			step="any"
																			value={ppManualValue}
																			onChange={(e) =>
																				setPpManualValue(e.target.value)
																			}
																			className="w-20 h-8 text-sm bg-secondary border-transparent rounded-sm text-center"
																			placeholder="--"
																			autoFocus
																			disabled={ppSaving}
																			onKeyDown={(e) => {
																				if (e.key === "Enter")
																					handleSaveManualValue(ex.exercise_id);
																				if (e.key === "Escape") {
																					setEditingPpExercise(null);
																					setPpManualValue("");
																				}
																			}}
																		/>
																	)}
																	<span className="text-xs text-muted-foreground">
																		{getUnitLabel(ex.unit)}
																	</span>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm text-volt"
																		disabled={ppSaving}
																		onClick={() =>
																			handleSaveManualValue(ex.exercise_id)
																		}
																	>
																		{ppSaving ? (
																			<Loader2 className="w-3.5 h-3.5 animate-spin" />
																		) : (
																			<Check className="w-3.5 h-3.5" />
																		)}
																	</Button>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm text-muted-foreground"
																		disabled={ppSaving}
																		onClick={closeManualEditor}
																	>
																		<X className="w-3.5 h-3.5" />
																	</Button>
																</div>
															) : (
																<div className="flex items-center gap-2 shrink-0 ml-2">
																	{ex.value !== null &&
																	ex.value !== undefined ? (
																		<div className="text-right">
																			<p
																				className="font-heading text-lg"
																				style={{
																					color: readable(domain.color || chartColors.volt),
																				}}
																			>
																				{ex.unit === "minutes_seconds"
																					? formatMinutesSeconds(ex.value)
																					: ex.value}
																				<span className="text-xs text-muted-foreground ml-1">
																					{getUnitLabel(ex.unit)}
																				</span>
																			</p>
																			{/* Il Sinclair, se il modello lo usa (D6b): il
																			    massimale normalizzato sul peso. */}
																			{ex.coefficient?.value != null && (
																				<p className="text-[10px] text-muted-foreground">
																					{t(
																						ex.coefficient.kind === "ipf_gl"
																							? "ppGlValue"
																							: "ppSinclairValue",
																					).replace("{value}", ex.coefficient.value)}
																				</p>
																			)}
																		</div>
																	) : (
																		<p className="text-sm text-muted-foreground italic">
																			—
																		</p>
																	)}
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm"
																		onClick={() => openManualEditor(ex)}
																		title={t("enterManualValue")}
																	>
																		<Edit2 className="w-3.5 h-3.5" />
																	</Button>
																	{ex.source === "manual" && (
																		<Button
																			variant="ghost"
																			size="icon"
																			className="h-7 w-7 rounded-sm hover:text-red-500"
																			onClick={() =>
																				handleDeleteManualValue(ex.exercise_id)
																			}
																			title={t("removeManualValue")}
																		>
																			<Trash2 className="w-3 h-3" />
																		</Button>
																	)}
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm text-muted-foreground hover:text-volt"
																		onClick={() =>
																			handleOpenPerformanceHistory(ex)
																		}
																		title={t("history")}
																	>
																		<Clock className="w-3.5 h-3.5" />
																	</Button>
																	{canViewPerformanceLeaderboard && (
																		<Button
																			variant="ghost"
																			size="icon"
																			className="h-7 w-7 rounded-sm text-yellow-500 hover:text-yellow-400"
																			onClick={() =>
																				handleOpenPerformanceLeaderboard(ex)
																			}
																			title={t("viewLeaderboardLabel")}
																		>
																			<Trophy className="w-3.5 h-3.5" />
																		</Button>
																	)}
																</div>
															)}
														</div>
													</CardContent>
												</Card>
											);
										})(),
									)}
								</div>
							) : (
								<p className="text-xs text-muted-foreground py-2">
									Nessun esercizio configurato per questo dominio
								</p>
							)}
						</div>
					))
				) : (
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="flex flex-col items-center justify-center py-12 text-center">
							<Target className="w-10 h-10 text-muted-foreground mb-3" />
							<p className="font-heading text-base mb-1">Profilo Prestativo</p>
							<p className="text-xs text-muted-foreground">
								{t("performanceProfilePending")}
								dall'admin
							</p>
						</CardContent>
					</Card>
				)}
			</div>

			<LeaderboardDialog
				open={ppLeaderboardOpen}
				onOpenChange={(open) => {
					setPpLeaderboardOpen(open);
					if (!open) {
						setPpLeaderboardExercise(null);
						setPpLeaderboardData(null);
						setPpLeaderboardError("");
						setPpLeaderboardGenderFilter("all");
						setPpLeaderboardGroupId("");
						setPpLeaderboardClassFilter("");
						setPpLeaderboardSort("value");
						setPpLeaderboardBodyweight(null);
					}
				}}
				title={t("performanceLeaderboardTitle")}
				description={
					ppLeaderboardData?.exercise_name ||
					ppLeaderboardExercise?.exercise_name
				}
				loading={ppLeaderboardLoading}
				error={ppLeaderboardError}
				data={ppLeaderboardData}
				genderFilter={ppLeaderboardGenderFilter}
				onGenderFilterChange={setPpLeaderboardGenderFilter}
				// Una categoria di peso e' gia' di un genere: i tab di genere si
				// tolgono, e ripartono da «tutti» quando si torna a tutte.
				hideGenderFilter={!!ppLeaderboardClassFilter}
				extraFilters={
					<LeaderboardBodyweightFilters
						info={ppLeaderboardBodyweight}
						classFilter={ppLeaderboardClassFilter}
						onClassFilterChange={(key) => {
							setPpLeaderboardGenderFilter("all");
							setPpLeaderboardClassFilter(key);
						}}
						sort={ppLeaderboardSort}
						onSortChange={setPpLeaderboardSort}
					/>
				}
				scopeValue={ppLeaderboardGroupId}
				onScopeChange={setPpLeaderboardGroupId}
				scopeOptions={[
					{ value: "", label: t("performanceLeaderboardSportScope") },
					...performanceGroups.map((group) => ({
						value: group.group_id,
						label: group.name,
					})),
				]}
				formatMetric={(entry) => {
					const byPoints = coefficientMetric(entry, ppLeaderboardSort, t, language);
					if (byPoints) return byPoints;
					if (ppLeaderboardExercise?.conditioning_type === "amrap") {
						const amrapLabel = formatAmrapScoreWithRoundsLabel(
							entry?.value,
							ppLeaderboardExercise?.conditioning_exercises || [],
							language,
						);
						if (amrapLabel) return amrapLabel;
					}
					if (entry.unit === "kg" && entry.bodyweight_ratio) {
						return (
							<span>
								{entry.metric_label || entry.value}
								<span className="text-xs text-muted-foreground ml-1.5">
									· {entry.bodyweight_ratio}
									{t("bodyweightRatioShort")}
								</span>
							</span>
						);
					}
					return entry.metric_label || entry.value;
				}}
				getEntryMetaLabel={(entry) =>
					`${entry.source === "manual" ? t("manualLabel") : t("fromDiary")}${entry.date ? ` · ${entry.date}` : ""}`
				}
				t={t}
				emptyDescription={t("performanceLeaderboardEmpty")}
				contentClassName="max-w-md w-[calc(100vw-2rem)] max-h-[80vh]"
			/>

			<Dialog
				open={ppHistoryOpen}
				onOpenChange={(open) => {
					setPpHistoryOpen(open);
					if (!open) {
						setPpHistoryExercise(null);
						setPpHistoryData(null);
						setPpHistoryError("");
						setPpHistoryEditingId(null);
						setPpHistoryRange("all");
						setPpHistoryDraft({
							date: "",
							value: "",
							raw_weight: "",
							raw_reps: "",
							notes: "",
						});
					}
				}}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-2xl p-3 sm:p-6 max-h-[92svh] overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader className="space-y-1.5 pr-7">
						<DialogTitle className="font-heading flex items-center gap-2">
							<Clock className="w-4 h-4 text-volt" />
							{t("performanceHistoryTitle")}
						</DialogTitle>
						<p className="text-xs text-muted-foreground break-words">
							{ppHistoryData?.exercise_name || ppHistoryExercise?.exercise_name}
							{ppHistoryData?.unit
								? ` · ${getUnitLabel(ppHistoryData.unit)}`
								: ""}
						</p>
						<p className="text-xs text-muted-foreground">
							{t("performanceHistoryDescription")}
						</p>
					</DialogHeader>

					{ppHistoryLoading ? (
						<div className="flex justify-center py-8">
							<div className="spinner"></div>
						</div>
					) : ppHistoryError ? (
						<p className="text-sm text-red-400">
							{t("historyLoadError")}: {ppHistoryError}
						</p>
					) : ppHistoryData ? (
						<div className="space-y-3 min-h-0">
							<div className="grid grid-cols-2 gap-1.5 sm:flex sm:gap-1 pb-1">
								<Button
									variant={ppHistoryRange === "30d" ? "default" : "secondary"}
									size="sm"
									className={`h-8 rounded-sm w-full sm:w-auto text-xs ${
										ppHistoryRange === "30d" ? "bg-volt hover:bg-volt/90" : ""
									}`}
									onClick={() => setPpHistoryRange("30d")}
								>
									{t("historyFilter30Days")}
								</Button>
								<Button
									variant={ppHistoryRange === "6m" ? "default" : "secondary"}
									size="sm"
									className={`h-8 rounded-sm w-full sm:w-auto text-xs ${
										ppHistoryRange === "6m" ? "bg-volt hover:bg-volt/90" : ""
									}`}
									onClick={() => setPpHistoryRange("6m")}
								>
									{t("historyFilter6Months")}
								</Button>
								<Button
									variant={ppHistoryRange === "1y" ? "default" : "secondary"}
									size="sm"
									className={`h-8 rounded-sm w-full sm:w-auto text-xs ${
										ppHistoryRange === "1y" ? "bg-volt hover:bg-volt/90" : ""
									}`}
									onClick={() => setPpHistoryRange("1y")}
								>
									{t("historyFilter1Year")}
								</Button>
								<Button
									variant={ppHistoryRange === "all" ? "default" : "secondary"}
									size="sm"
									className={`h-8 rounded-sm w-full sm:w-auto text-xs ${
										ppHistoryRange === "all" ? "bg-volt hover:bg-volt/90" : ""
									}`}
									onClick={() => setPpHistoryRange("all")}
								>
									{t("historyFilterAll")}
								</Button>
							</div>

							{ppHistoryChartData.length > 1 && (
								<div className="space-y-2">
									{ppHistoryData.unit === "kg" && (
										<div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
											<div className="flex items-center gap-1.5">
												<span className="w-2.5 h-2.5 rounded-full bg-volt" />
												<span>{t("historyWeight")}</span>
											</div>
											<div className="flex items-center gap-1.5">
												<span className="w-2.5 h-2.5 rounded-full bg-acid" />
												<span>{t("historyEstimated1rm")}</span>
											</div>
										</div>
									)}
									<div className="h-[150px] sm:h-[190px]">
										<ResponsiveContainer width="100%" height="100%">
											<LineChart data={ppHistoryChartData}>
												<CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
												<XAxis
													dataKey="date"
													stroke={chartColors.axis}
													tick={{ fill: chartColors.axis, fontSize: 10 }}
												/>
												<YAxis
													stroke={chartColors.axis}
													tick={{ fill: chartColors.axis, fontSize: 10 }}
													width={35}
												/>
												<Tooltip
													contentStyle={tooltipStyle}
													formatter={(v, name, entry) => {
														if (ppHistoryData.unit === "kg") {
															const label =
																name === "weight"
																	? t("historyWeight")
																	: t("historyEstimated1rm");
															const repsSuffix =
																name === "weight" && entry?.payload?.reps
																	? ` x${entry.payload.reps}`
																	: "";
															return [`${v} kg${repsSuffix}`, label];
														}
														return [`${v}`, getUnitLabel(ppHistoryData.unit)];
													}}
												/>
												{ppHistoryData.unit === "kg" ? (
													<>
														<Line
															type="monotone"
															dataKey="weight"
															name="weight"
															stroke={chartColors.blue}
															strokeWidth={2}
															dot={{ fill: chartColors.blue, r: 3 }}
														/>
														<Line
															type="monotone"
															dataKey="estimated1rm"
															name="estimated1rm"
															stroke={chartColors.acid}
															strokeWidth={2}
															dot={{ fill: chartColors.acid, r: 3 }}
														/>
													</>
												) : (
													<Line
														type="monotone"
														dataKey="value"
														stroke={chartColors.volt}
														strokeWidth={2}
														dot={{ fill: chartColors.volt, r: 3 }}
													/>
												)}
											</LineChart>
										</ResponsiveContainer>
									</div>
								</div>
							)}

							{ppHistoryItems.length > 0 ? (
								<div className="space-y-2 max-h-[38svh] sm:max-h-96 overflow-y-auto pr-1">
									{ppHistoryItems.map((entry) => {
										const entryKey = getPpHistoryItemKey(entry);
										return (
											<div
												key={entryKey}
												className="p-3 bg-secondary rounded-sm"
											>
												{ppHistoryEditingId === entryKey ? (
													<div className="space-y-2">
														<Input
															type="date"
															value={ppHistoryDraft.date}
															onChange={(e) =>
																setPpHistoryDraft({
																	...ppHistoryDraft,
																	date: e.target.value,
																})
															}
															className="bg-card border-border rounded-sm h-10"
														/>
														{ppHistoryData.unit === "kg" ? (
															<div className="grid grid-cols-2 gap-2">
																<Input
																	type="number"
																	step="0.1"
																	placeholder="Weight (kg)"
																	value={ppHistoryDraft.raw_weight}
																	onChange={(e) =>
																		setPpHistoryDraft({
																			...ppHistoryDraft,
																			raw_weight: e.target.value,
																		})
																	}
																	className="bg-card border-border rounded-sm h-10"
																/>
																<Input
																	type="number"
																	placeholder="Reps"
																	value={ppHistoryDraft.raw_reps}
																	onChange={(e) =>
																		setPpHistoryDraft({
																			...ppHistoryDraft,
																			raw_reps: e.target.value,
																		})
																	}
																	className="bg-card border-border rounded-sm h-10"
																/>
															</div>
														) : (
															<Input
																type="number"
																step="any"
																placeholder="Value"
																value={ppHistoryDraft.value}
																onChange={(e) =>
																	setPpHistoryDraft({
																		...ppHistoryDraft,
																		value: e.target.value,
																	})
																}
																className="bg-card border-border rounded-sm h-10"
															/>
														)}
														<Input
															placeholder="Notes"
															value={ppHistoryDraft.notes}
															onChange={(e) =>
																setPpHistoryDraft({
																	...ppHistoryDraft,
																	notes: e.target.value,
																})
															}
															className="bg-card border-border rounded-sm h-10"
														/>
														<div className="flex gap-2">
															<Button
																variant="default"
																size="sm"
																className="flex-1 bg-volt hover:bg-volt/90 rounded-sm"
																disabled={ppHistorySavingId === entryKey}
																onClick={() => handleSaveHistoryItem(entry)}
															>
																{ppHistorySavingId === entryKey
																	? t("savingLabel")
																	: t("saveChanges")}
															</Button>
															<Button
																variant="secondary"
																size="sm"
																className="flex-1 rounded-sm"
																onClick={() => setPpHistoryEditingId(null)}
															>
																{t("cancel")}
															</Button>
														</div>
													</div>
												) : (
													<div className="flex items-center justify-between gap-2">
														<div className="min-w-0">
															<p className="text-sm font-medium">
																{entry.date || "No date"}
															</p>
															<p className="text-xs text-muted-foreground">
																{renderHistoryEntryValue(
																	entry,
																	ppHistoryData.unit,
																)}
															</p>
															<HistoryEntrySource
																entry={entry}
																onOpen={
																	entry.result_id
																		? () => setPpSessionResultId(entry.result_id)
																		: undefined
																}
															/>
															{entry.notes && (
																<p className="text-xs text-muted-foreground mt-1">
																	{entry.notes}
																</p>
															)}
														</div>
														{(entry.result_id || entry.can_edit) && (
															<div className="flex gap-1 shrink-0">
																{entry.result_id && (
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-10 w-10 rounded-sm"
																		title={t("sessionDetailButton")}
																		aria-label={t("sessionDetailButton")}
																		onClick={() =>
																			setPpSessionResultId(entry.result_id)
																		}
																	>
																		<ClipboardList className="w-3.5 h-3.5" />
																	</Button>
																)}
																{entry.can_edit && (
																	<>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-10 w-10 rounded-sm"
																		onClick={() =>
																			handleStartEditHistoryItem(entry)
																		}
																	>
																		<Edit2 className="w-3.5 h-3.5" />
																	</Button>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-10 w-10 rounded-sm text-muted-foreground hover:text-blaze hover:bg-blaze/10"
																		disabled={ppHistorySavingId === entryKey}
																		onClick={() => handleDeleteHistoryItem(entry)}
																	>
																		<Trash2 className="w-3 h-3" />
																	</Button>
																	</>
																)}
															</div>
														)}
													</div>
												)}
											</div>
										);
									})}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									{t("noDataAvailable")}
								</p>
							)}
						</div>
					) : null}
				</DialogContent>
			</Dialog>

			<SessionDetailDialog
				open={!!ppSessionResultId}
				onOpenChange={(open) => {
					if (!open) setPpSessionResultId(null);
				}}
				resultId={ppSessionResultId}
				exerciseName={
					ppHistoryData?.exercise_name || ppHistoryExercise?.exercise_name
				}
			/>

			<Dialog
				open={!!mediaExercise}
				onOpenChange={(open) => {
					if (!open) setMediaExercise(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-xl">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{mediaExercise?.exercise_name || mediaExercise?.name}
						</DialogTitle>
					</DialogHeader>
					{mediaExercise?.video_url &&
					getYouTubeEmbedUrl(mediaExercise.video_url) ? (
						<div className="aspect-video rounded-sm overflow-hidden">
							<iframe
								src={getYouTubeEmbedUrl(mediaExercise.video_url)}
								title={mediaExercise?.exercise_name || mediaExercise?.name}
								className="w-full h-full"
								allowFullScreen
							/>
						</div>
					) : mediaExercise?.video_url ? (
						<a
							href={mediaExercise.video_url}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 text-volt hover:underline text-sm p-3 bg-secondary/50 rounded-sm"
						>
							<ExternalLink className="w-4 h-4" />
							Apri video esterno
						</a>
					) : mediaExercise?.image_url ? (
						<img
							src={getImageUrl(mediaExercise.image_url)}
							alt={
								mediaExercise?.exercise_name || mediaExercise?.name || "media"
							}
							className="w-full rounded-sm"
						/>
					) : null}
					{mediaExercise?.description && (
						<p className="text-sm text-muted-foreground">
							{mediaExercise.description}
						</p>
					)}
				</DialogContent>
			</Dialog>

			<Dialog
				open={!!conditioningPreviewExercise}
				onOpenChange={(open) => {
					if (!open) setConditioningPreviewExercise(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading flex items-center gap-2">
							<Zap className="w-4 h-4 text-yellow-400" />
							{conditioningPreviewExercise?.exercise_name ||
								conditioningPreviewExercise?.name ||
								t("conditioning")}
						</DialogTitle>
					</DialogHeader>
					{(() => {
						const cfg =
							conditioningPreviewExercise?.conditioning_config ||
							conditioningPreviewExercise ||
							null;
						const condType = String(
							cfg?.conditioning_type ||
								conditioningPreviewExercise?.conditioning_type ||
								"",
						)
							.trim()
							.toLowerCase();
						const condExercises = Array.isArray(cfg?.conditioning_exercises)
							? cfg.conditioning_exercises
							: Array.isArray(
										conditioningPreviewExercise?.conditioning_exercises,
								  )
								? conditioningPreviewExercise.conditioning_exercises
								: [];
						const emomRounds =
							getDisplayEmomRounds(cfg).length > 0
								? getDisplayEmomRounds(cfg)
								: getDisplayEmomRounds(conditioningPreviewExercise);
						const intervalLabel = formatIntervalSecondsLabel(
							cfg?.interval_seconds,
							language,
						);
						const freeCustomText = String(
							cfg?.conditioning_free_text ||
								cfg?.custom_workout_text ||
								conditioningPreviewExercise?.conditioning_free_text ||
								conditioningPreviewExercise?.custom_workout_text ||
								"",
						).trim();

						const resolveFromLibrary = (exerciseId, exerciseName) => {
							const byId = exerciseId ? exerciseLibraryMap[exerciseId] : null;
							if (byId) return byId;
							const byName = String(exerciseName || "")
								.trim()
								.toLowerCase();
							return byName ? exerciseLibraryMap[`_name_${byName}`] : null;
						};

						return (
							<div className="space-y-3">
								<div className="flex flex-wrap gap-2">
									{condType && (
										<Badge
											variant="outline"
											className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs rounded-sm"
										>
											{condType.toUpperCase()}
										</Badge>
									)}
									{cfg?.time_cap_seconds && (
										<Badge variant="outline" className="text-xs rounded-sm">
											TC {Math.floor(cfg.time_cap_seconds / 60)}'
										</Badge>
									)}
									{cfg?.rounds && (
										<Badge variant="outline" className="text-xs rounded-sm">
											{cfg.rounds} {cfg.rounds === 1 ? "round" : "rounds"}
										</Badge>
									)}
									{intervalLabel && (
										<Badge variant="outline" className="text-xs rounded-sm">
											{t("everyLabel")} {intervalLabel}
										</Badge>
									)}
								</div>

								{condType === "emom" && emomRounds.length > 0 ? (
									<div className="space-y-2">
										{emomRounds.map((round, roundIndex) => (
											<div
												key={round.round_id || roundIndex}
												className="space-y-2 bg-yellow-500/10 border border-yellow-500/20 rounded-sm px-3 py-3"
											>
												<div className="text-[10px] uppercase tracking-wider text-yellow-400 font-medium">
													{getEmomRoundLabel(round, roundIndex, language)}
												</div>
												<div className="space-y-1.5">
													{(round.items || []).map((item, itemIndex) => {
														const libItem = item.is_custom
															? null
															: resolveFromLibrary(
																	item.exercise_id,
																	item.exercise_name,
																);
														const hasMedia =
															Boolean(libItem?.video_url) ||
															Boolean(libItem?.image_url);
														return (
															<div
																key={item.item_id || itemIndex}
																className="flex items-start gap-2 text-xs bg-background/50 rounded-sm px-3 py-2"
															>
																<div className="flex-1 min-w-0">
																	<div className="font-medium">
																		{formatEmomItemSummary(item, language)}
																	</div>
																	{item.notes && (
																		<div className="text-[10px] text-muted-foreground italic">
																			{item.notes}
																		</div>
																	)}
																</div>
																{hasMedia && (
																	<button
																		type="button"
																		onClick={() =>
																			setMediaExercise({
																				...item,
																				...libItem,
																			})
																		}
																		className="shrink-0 w-7 h-7 rounded-sm bg-yellow-500/10 flex items-center justify-center"
																	>
																		{libItem?.video_url ? (
																			<Play className="w-3.5 h-3.5 text-yellow-400" />
																		) : (
																			<Image className="w-3.5 h-3.5 text-yellow-400" />
																		)}
																	</button>
																)}
															</div>
														);
													})}
												</div>
											</div>
										))}
									</div>
								) : (
									condExercises.length > 0 && (
										<div className="space-y-1">
											{condExercises.map((ce, i) => {
												const libItem = resolveFromLibrary(
													ce.exercise_id,
													ce.exercise_name || ce.name,
												);
												const hasMedia =
													Boolean(libItem?.video_url) ||
													Boolean(libItem?.image_url);
												return (
													<div
														key={i}
														className="flex items-center gap-2 text-xs bg-secondary/50 rounded-sm px-3 py-2"
													>
														<div className="flex-1 min-w-0">
															{ce.reps && (
																<span className="font-medium mr-1">
																	{ce.reps}
																</span>
															)}
															{ce.exercise_name || ce.name}
															{ce.load && (
																<span className="text-muted-foreground ml-2">
																	{ce.load}
																</span>
															)}
															{ce.notes && (
																<span className="text-muted-foreground ml-2 text-[10px] italic">
																	{ce.notes}
																</span>
															)}
														</div>
														{hasMedia && (
															<button
																type="button"
																onClick={() =>
																	setMediaExercise({
																		...ce,
																		...libItem,
																	})
																}
																className="shrink-0 w-7 h-7 rounded-sm bg-volt/10 flex items-center justify-center"
															>
																{libItem?.video_url ? (
																	<Play className="w-3.5 h-3.5 text-volt" />
																) : (
																	<Image className="w-3.5 h-3.5 text-volt" />
																)}
															</button>
														)}
													</div>
												);
											})}
										</div>
									)
								)}

								{freeCustomText && (
									<div className="text-xs bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap">
										{freeCustomText}
									</div>
								)}

								{condExercises.length === 0 &&
									emomRounds.length === 0 &&
									!freeCustomText && (
										<div className="text-xs text-muted-foreground flex items-center gap-2">
											<Dumbbell className="w-3.5 h-3.5" />
											<span>{t("noDataAvailable")}</span>
										</div>
									)}
							</div>
						);
					})()}
				</DialogContent>
			</Dialog>
		</Layout>
	);
}
